import React, {Component} from "react";
import "./index.less";
import * as THREE from 'three';
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import {Object3D} from "three/src/core/Object3D";
import {Vector3} from "three/src/math/Vector3";
import logoImage from './assets/logo.png';
import authImage from './assets/auth.png';
import backHeaderImage from './assets/back-header.jpg';
import backBodyImage from './assets/back-body.jpg';
import SceneInit from "./SceneInit";

interface IProps {
    collectionName: string,
    nftTokenId: string,
    nftImageUrl: string,
    width: number,
    height: number,
    reCreate?:boolean
}

interface IStates {
}

const canvasId = "mythModalCanvas";

class IPL3DModel extends Component<IProps, IStates> {
    //card measure sizes
    private cardWidth = 312;
    private cardHeight = 498;
    private paddingTop = 28;
    private paddingLeft = 22;
    private headerWidth = this.cardWidth - this.paddingLeft * 2;
    private headerHeight = 82;
    private bodyWidth = this.headerWidth;
    private bodyHeight = 356;
    private bodyMarginTop = 14;
    private offsetY = 32;

    private sceneInit: SceneInit | null = null;

    async createFrontMesh(sceneInit: any, modalScene: Object3D) {
        const {nftTokenId, collectionName, nftImageUrl} = this.props;

        const modelSize = this.getSize(modalScene);
        this.drawFrontHeader(nftTokenId, collectionName, 'IP-Licensing Card', sceneInit.scene,
            modelSize, this.headerHeight, this.headerWidth, this.cardWidth, this.cardHeight, this.offsetY);

        if(nftImageUrl === ""){
            return
        }
        const imageLoader = new THREE.ImageLoader();
        const image = await imageLoader.loadAsync(nftImageUrl);
        const canvas = document.createElement('canvas');
        const ratio = this.bodyWidth / this.bodyHeight;
        canvas.width = image.width * ratio;
        canvas.height = image.height;
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }

        context.drawImage(image, (1 - ratio) / 2 * image.width, 0, image.width * ratio, image.height, 0, 0, canvas.width, canvas.height);
        const geometryWidth = modelSize.x * (this.bodyWidth / this.cardWidth);
        const geometryHeight = modelSize.y * (this.bodyHeight / this.cardHeight);
        const geometry = new THREE.PlaneGeometry(geometryWidth, geometryHeight);
        const material = new THREE.MeshBasicMaterial({});
        const mesh = new THREE.Mesh(geometry, material);
        const texture = new THREE.CanvasTexture(canvas);
        texture.encoding = THREE.sRGBEncoding;
        material.map = texture;
        material.needsUpdate = true;
        material.transparent = true;

        const y = -this.offsetY / this.cardHeight / 2;
        mesh.position.set(0, y, 0.01);

        sceneInit.scene.add(mesh);
    }

    drawFrontHeader(tokenId: string, collectionName: string, label: string, scene: THREE.Scene,
                    modelSize: Vector3, headerHeight: number, headerWidth: number,
                    cardWidth: number, cardHeight: number, offsetY: number) {
        const logo = new Image();
        logo.onload = function () {
            //init canvas
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context) {
                return;
            }
            canvas.height = canvas.width * (headerHeight / headerWidth);
            context.fillStyle = 'rgba(0,0,0,1)';
            context.fillRect(0, 0, canvas.width, canvas.height);

            //draw header
            context.fillStyle = 'rgba(255,255,255,1)';
            const fontSize = canvas.height * 0.2;
            const headerPaddingLeft = canvas.width * 0.04;
            const fontMarginTop = canvas.height * 0.08;
            context.font = fontSize + 'px Arial';
            let dy = fontSize + fontMarginTop;
            context.fillText(tokenId, headerPaddingLeft, dy);

            //draw auth icon
            const iconHeight = fontSize;
            const iconWidth = iconHeight;
            const authIcon = new Image();
            authIcon.onload = function () {
                context.drawImage(authIcon, headerPaddingLeft, dy + fontMarginTop + fontSize * 0.1, iconWidth, iconHeight);
                dy += fontSize + fontMarginTop;
                const dx = headerPaddingLeft + iconWidth + 5;
                context.fillText(collectionName, dx, dy);
                dy += fontSize + fontMarginTop;
                context.fillText(label, headerPaddingLeft, dy);

                //draw logo
                const logoHeight = canvas.height / 2;
                const logoWidth = logoHeight;
                const sx = canvas.width - logoWidth - canvas.width * 0.1;
                const sy = (canvas.height - logoHeight) / 2;
                context.drawImage(logo, sx, sy, logoWidth, logoHeight);

                const geometryWidth = modelSize.x * (headerWidth / cardWidth);
                const geometryHeight = modelSize.y * (headerHeight / cardHeight);
                const geometry = new THREE.PlaneGeometry(geometryWidth, geometryHeight);
                const material = new THREE.MeshBasicMaterial({});
                const mesh = new THREE.Mesh(geometry, material);
                const texture = new THREE.CanvasTexture(canvas);
                texture.encoding = THREE.sRGBEncoding;
                material.map = texture;
                material.needsUpdate = true;
                material.transparent = true;

                // const y = (cardHeight + offsetY) / 2 / headerHeight;
                // console.log('y=', y);
                mesh.position.set(0, 1.01, 0.01);
                scene.add(mesh);
            };
            authIcon.src = authImage
        };
        logo.src = logoImage;
    }

    async createBackMesh(sceneInit: any, modalScene: Object3D) {
        const modelSize = this.getSize(modalScene);

        //header
        let imageLoader = new THREE.ImageLoader();
        let image = await imageLoader.loadAsync(backHeaderImage);
        let geometryWidth = modelSize.x * (this.headerWidth / this.cardWidth);
        let geometryHeight = modelSize.y * (this.headerHeight / this.cardHeight);
        let geometry = new THREE.PlaneGeometry(geometryWidth, geometryHeight);
        let material = new THREE.MeshBasicMaterial({side: THREE.BackSide});
        let mesh = new THREE.Mesh(geometry, material);
        let texture = new THREE.CanvasTexture(image);
        texture.encoding = THREE.sRGBEncoding;
        material.map = texture;
        material.needsUpdate = true;
        material.transparent = true;
        mesh.position.set(0, 1.01, -0.01);
        sceneInit.scene.add(mesh);

        //body
        imageLoader = new THREE.ImageLoader();
        image = await imageLoader.loadAsync(backBodyImage);
        geometryWidth = modelSize.x * (this.bodyWidth / this.cardWidth);
        geometryHeight = modelSize.y * (this.bodyHeight / this.cardHeight);
        geometry = new THREE.PlaneGeometry(geometryWidth, geometryHeight);
        material = new THREE.MeshBasicMaterial({side: THREE.BackSide});
        mesh = new THREE.Mesh(geometry, material);
        texture = new THREE.CanvasTexture(image);
        texture.encoding = THREE.sRGBEncoding;
        material.map = texture;
        material.needsUpdate = true;
        material.transparent = true;
        const y = -this.offsetY / this.cardHeight / 2;
        mesh.position.set(0, y, -0.01);
        sceneInit.scene.add(mesh);
    }

    getSize(model: Object3D): Vector3 {
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        return size;
    }

    createModal = async () => {
        const {width, height} = this.props;
        try {
            this.sceneInit = new SceneInit(canvasId);
            this.sceneInit.initialize(width, height);
            this.sceneInit.animate();

            const loader = new GLTFLoader();
            const modal = await loader.loadAsync("https://server.myth.art/modal.glb");
            const modalScene = modal.scene;
            const modelScale = 0.2;
            modalScene.scale.set(modelScale, modelScale, modelScale);
            if (this.sceneInit.scene) {
                this.sceneInit.scene.add(modalScene);

                await this.createFrontMesh(this.sceneInit, modalScene);

                await this.createBackMesh(this.sceneInit, modalScene);
            }
        } catch (e) {
            console.error(e);
        }
    };

    componentDidMount(): void {
        this.createModal();
    }

    componentDidUpdate(prevProps: IProps): void {
        if (prevProps.width !== this.props.width || this.props.reCreate) this.createModal();
    }

    componentWillUnmount(): void {
        //release memory
        if (this.sceneInit) {
            this.sceneInit.destroy();
        }
    }

    render() {
        return <canvas id={canvasId}/>;
    }
}

export default IPL3DModel;
